import React, { useCallback, useEffect, useLayoutEffect, useMemo } from "react";
import { useFragment, useLazyLoadQuery, useMutation } from "react-relay";
import { graphql } from "relay-runtime";
import { UserLoggedInNavigatorScreenProps } from "../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { withWebModalStack } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModalStack";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { SignalDetailScreenQuery } from "./__generated__/SignalDetailScreenQuery.graphql";
import { SignalDetailScreenContent$key } from "./__generated__/SignalDetailScreenContent.graphql";
import {
  AHIcon,
  AHSeparator,
  AHText,
  Box,
  OrgIcon,
  SubjectBreadcrumbs,
  useBoxStyle,
  useTheme,
} from "arkh";
import { ContentTitlePreview } from "../../components/ContentLitstItem";
import { Platform, ScrollView } from "react-native";
import { SignalDetailFooter } from "./SignalDetailFooter";
import { StoreBanner } from "../../components/OrgHeaders/StoreBanner";
import { HeaderButton } from "../../components";
import { Header } from "@react-navigation/elements";
import { StoreFormattedDate } from "../../common/dates/StoreFormattedDate";
import { TimeZoneProvider } from "../../OS/Timezone";
import { LinkedContent } from "../../components/LinkedContent/LinkedContent";

import { usePersistedEmployeeId } from "@/src/OS/Login/usePersistedEmployeeId";
import { SignalDetailScreenRegisterAsSeenEmployeeMutation } from "./__generated__/SignalDetailScreenRegisterAsSeenEmployeeMutation.graphql";
import { SignalDetailScreenRegisterAsSeenMutation } from "./__generated__/SignalDetailScreenRegisterAsSeenMutation.graphql";
import { SignalDetailProducts } from "./SignalDetailProducts";
import { useSizeClass } from "@/src/common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { RichTextViewer } from "@/src/components/RichText";

export const SignalDetailScreen = withWebModalStack(
  (props: UserLoggedInNavigatorScreenProps<"SignalDetail">) => {
    useLayoutEffect(() => {
      props.navigation.setOptions({
        headerShown: false,
      });
    }, [props.navigation]);
    const render = useCallback(
      (fetchKey: string) => (
        <SignalDetail
          fetchKey={fetchKey}
          signalId={props.route.params.signalId}
          onShowFile={(id) => {
            props.navigation.navigateDeprecated("File", { id });
          }}
          openSignalOpenedBy={() =>
            props.navigation.navigateDeprecated("SignalOpenedBy", {
              signalId: props.route.params.signalId,
              storeName: props.route.params.storeName,
            })
          }
          openSignalOpenedByEmployee={() =>
            props.navigation.navigateDeprecated("SignalOpenedByEmployee", {
              signalId: props.route.params.signalId,
              storeName: props.route.params.storeName,
            })
          }
          openProduct={(id) => {
            props.navigation.navigateDeprecated("Product", {
              screen: "ProductMain",
              params: {
                id,
                type: "id",
                state: "details",
              },
            });
          }}
        />
      ),
      [
        props.route.params.signalId,
        props.route.params.storeName,
        props.navigation,
      ]
    );
    return (
      <TimeZoneProvider timezone={props.route.params.storeTimezone}>
        <Box style={{ flex: 1 }}>
          {props.route.params.storeName ? (
            <StoreBanner storeName={props.route.params.storeName} />
          ) : null}
          <Box spaceHorizontal={Platform.OS === "web" ? "none" : "medium"}>
            <Header
              headerStatusBarHeight={
                Platform.OS === "android" || props.route.params.storeName
                  ? undefined
                  : 0
              }
              title=""
              headerLeft={() => (
                <HeaderButton type="CLOSE" onPress={props.navigation.goBack} />
              )}
            />
          </Box>
          <LazyQueryNetworkBoundrary>{render}</LazyQueryNetworkBoundrary>
        </Box>
      </TimeZoneProvider>
    );
  },
  true
);

function SignalDetail(props: {
  signalId: string;
  fetchKey: string;
  onShowFile: (id: string) => void;
  openSignalOpenedBy: () => void;
  openSignalOpenedByEmployee: () => void;
  openProduct: (id: string) => void;
}) {
  const data = useLazyLoadQuery<SignalDetailScreenQuery>(
    graphql`
      query SignalDetailScreenQuery($signalId: ID!) {
        me {
          currentStoreUser {
            employee {
              id
            }
          }
        }
        signal(id: $signalId) {
          id
          ...SignalDetailScreenContent
          ...SignalDetailFooter_signal
          linkedProducts {
            ...SignalDetailProducts_StoreProducts
          }
        }
      }
    `,
    {
      signalId: props.signalId,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const contentContainerStyle = useBoxStyle({
    space: "medium",
    color: "surface-background-light",
  });

  const theme = useTheme();
  const isCompact = useSizeClass().width === SizeClass.COMPACT;
  if (!data.signal) return null;

  return (
    <>
      <ScrollView
        style={{
          backgroundColor: theme["surface-background-light"],
        }}
        contentContainerStyle={contentContainerStyle}
        keyboardShouldPersistTaps="handled"
      >
        <Box
          cornerRadius="large"
          borderColor="border-primary"
          style={{ borderWidth: 1 }}
        >
          <SignalDetailContent
            employeeId={data.me?.currentStoreUser?.employee?.id}
            signal={data.signal}
            onShowFile={props.onShowFile}
          />
          {data.signal.linkedProducts.length > 0 ? (
            <>
              <AHSeparator />
              <SignalDetailProducts
                products={data.signal.linkedProducts}
                compact={isCompact}
                onPressProduct={(id) => {
                  props.openProduct(id);
                }}
                todoId={data.signal.id}
              />
            </>
          ) : null}
        </Box>
      </ScrollView>
      <SignalDetailFooter
        signal={data.signal}
        openSeenBy={() => {
          props.openSignalOpenedBy();
        }}
        openSeenByEmployee={() => {
          props.openSignalOpenedByEmployee();
        }}
      />
    </>
  );
}

function SignalDetailContent(props: {
  employeeId?: string;
  signal: SignalDetailScreenContent$key;
  onShowFile: (id: string) => void;
}) {
  const signal = useFragment(
    graphql`
      fragment SignalDetailScreenContent on Signal {
        id

        title
        orgIconUri
        body
        formattedBody
        descriptionImages {
          id
          ...LinkedContent_images
        }
        descriptionFiles {
          id
          name
          type
          url
          ...LinkedContent_files
        }

        updatedAtDate {
          ...StoreFormattedDate
        }

        breadcrumbs
      }
    `,
    props.signal
  );
  const [registerAsSeenEmployee] =
    useMutation<SignalDetailScreenRegisterAsSeenEmployeeMutation>(graphql`
      mutation SignalDetailScreenRegisterAsSeenEmployeeMutation(
        $signalWorkspaceId: ID!
        $employeeId: ID
      ) {
        signal(id: $signalWorkspaceId) {
          registerEmployeeReceiverReceipt(employeeId: $employeeId) {
            id
            opened
            employeeReceiverReceiptsCount
            employeeReceiverReceipts {
              signalWorkspaceId
              employeeId
              openedAtDate {
                ...FormattedDate
              }
            }
          }
        }
      }
    `);
  const [registerAsSeen] =
    useMutation<SignalDetailScreenRegisterAsSeenMutation>(graphql`
      mutation SignalDetailScreenRegisterAsSeenMutation(
        $signalWorkspaceId: ID!
      ) {
        signal(id: $signalWorkspaceId) {
          registerReceiverReceipt {
            id
            opened
            receiverReceipts {
              signalWorkspaceId
              userId

              openedAtDate {
                ...FormattedDate
              }
            }
            receiverReceiptsCount
          }
        }
      }
    `);

  const { currentEmployeeId } = usePersistedEmployeeId();
  useEffect(() => {
    registerAsSeenEmployee({
      variables: {
        signalWorkspaceId: signal.id,
        employeeId: currentEmployeeId,
      },
    });
  }, [signal.id, registerAsSeenEmployee, registerAsSeen, currentEmployeeId]);

  const hasFormattedBody = useMemo(() => {
    return (
      signal.formattedBody &&
      signal.formattedBody.length > 0 &&
      signal.formattedBody !== '{"type":"doc","content":[{"type":"paragraph"}]}'
    );
  }, [signal.formattedBody]);
  const hasLinkedContent = useMemo(() => {
    return (
      signal.descriptionFiles.length > 0 || signal.descriptionImages.length > 0
    );
  }, [signal.descriptionFiles, signal.descriptionImages]);
  return (
    <>
      <Box
        style={{ flexDirection: "row", alignItems: "center" }}
        spaceHorizontal="medium"
        spaceTop="medium"
      >
        <OrgIcon spaceRight="xsmall" orgIconUri={signal.orgIconUri} />
        <SubjectBreadcrumbs breadcrumbs={signal.breadcrumbs} />
        <Box style={{ flex: 1 }} />
        <AHText type="detail" color="text-washed" spaceRight="small">
          <StoreFormattedDate date={signal.updatedAtDate} />
        </AHText>

        <AHIcon type="locked" size="xsmall" color="text-washed" />
      </Box>

      <ContentTitlePreview title={signal.title} />
      {hasFormattedBody || hasLinkedContent || signal.body ? (
        <AHSeparator />
      ) : null}
      {hasFormattedBody ? (
        <Box space="medium" spaceBottom={hasLinkedContent ? "none" : "medium"}>
          <RichTextViewer
            data={signal.formattedBody}
            unformattedData={signal.body}
          />
        </Box>
      ) : signal.body ? (
        <AHText
          space="medium"
          spaceBottom={hasLinkedContent ? "none" : "medium"}
          type="body-big"
        >
          {signal.body}
        </AHText>
      ) : null}
      {hasLinkedContent ? (
        <LinkedContent
          files={signal.descriptionFiles}
          images={signal.descriptionImages}
          openFile={({ fileId }) => props.onShowFile(fileId)}
          borderColor="border-strong"
        />
      ) : null}
    </>
  );
}
